import { useEffect, useRef } from 'react';

import { BASE_NUGGET_WIDTH, FullScreenLoading } from '@backrs_ui/common-ui';

import { lottieAnimations } from '@backrs_ui/common-ui/src/assets/lottieAnimations';
import { DraggableNuggetCard } from '@backrs_ui/grow-ui/src/components/NuggetCards/DraggableNuggetCard';
import { PAGE_SCROLL_ID } from '@backrs_ui/grow-ui/src/components/PageShell/pageShell.layout';
import {
  HomeTab,
  useHomeTabNuggets,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/useHomeTabNuggets';

import { Box, SxProps, Theme } from '@mui/material';
import Lottie from 'lottie-react';

import { FeaturedHeader } from './FeaturedHeader';
import { useAgentContext } from './HomeTabContent.agentContext';

type HomeTabContentProps = Readonly<{
  tab: HomeTab;
}>;

export const HomeTabContent = ({ tab }: HomeTabContentProps) => {
  const { items, hasMore, isLoading, isLoadingMore, loadMore } = useHomeTabNuggets(tab);
  const hasUserScrolledRef = useRef(false);
  const loadMoreLockedRef = useRef(false);
  const hasAutoFilledRef = useRef(false);

  useAgentContext(tab, items);

  useEffect(() => {
    hasUserScrolledRef.current = false;
    hasAutoFilledRef.current = false;
    loadMoreLockedRef.current = false;
  }, [tab]);

  useEffect(() => {
    if (!isLoadingMore) {
      loadMoreLockedRef.current = false;
      hasAutoFilledRef.current = false;
    }

    const canLoadMore = () => hasMore && !isLoadingMore && !loadMoreLockedRef.current;

    const getFallbackScrollElement = (): HTMLElement | null =>
      (document.getElementById(PAGE_SCROLL_ID) as HTMLElement | null) ??
      (document.scrollingElement as HTMLElement | null);

    const maybeLoadMore = (scrollElement?: HTMLElement | null, allowWithoutScroll = false) => {
      const activeScrollElement = scrollElement ?? getFallbackScrollElement();

      if (!activeScrollElement) return false;

      const distanceToBottom =
        activeScrollElement.scrollHeight -
        activeScrollElement.scrollTop -
        activeScrollElement.clientHeight;

      const shouldLoad =
        canLoadMore() &&
        (hasUserScrolledRef.current || allowWithoutScroll) &&
        distanceToBottom < 200;

      if (shouldLoad) {
        loadMoreLockedRef.current = true;
        loadMore();

        return true;
      }

      return false;
    };

    const maybeAutoFill = () => {
      const scrollElement = getFallbackScrollElement();

      if (!scrollElement) return;

      const hasOverflow = scrollElement.scrollHeight > scrollElement.clientHeight + 1;

      if (!hasOverflow && !hasAutoFilledRef.current) {
        const didLoad = maybeLoadMore(scrollElement, true);

        if (didLoad) {
          hasAutoFilledRef.current = true;
        }
      }
    };

    const handleUserScroll = (event: Event) => {
      hasUserScrolledRef.current = true;

      const target = event.target;

      if (target instanceof HTMLElement) {
        maybeLoadMore(target);

        return;
      }

      maybeLoadMore();
    };

    document.addEventListener('scroll', handleUserScroll, true);
    maybeAutoFill();

    return () => {
      document.removeEventListener('scroll', handleUserScroll, true);
    };
  }, [hasMore, isLoadingMore, loadMore, tab]);

  if (isLoading) {
    return <FullScreenLoading fillContainer />;
  }

  return (
    <>
      {tab === 'FEATURED' && <FeaturedHeader />}
      {items.length > 0 && (
        <Box sx={gridSx}>
          {items.map((item) => (
            <DraggableNuggetCard
              key={item.key}
              nugget={item.nugget}
              resourceKey={item.type}
              resourceId={String(item.nugget.id)}
              fluid
            />
          ))}
        </Box>
      )}
      {isLoadingMore && (
        <Box sx={loadingMoreSx}>
          <Box sx={loadingMoreAnimationSx}>
            <Lottie animationData={lottieAnimations.dotsDotting} />
          </Box>
        </Box>
      )}
    </>
  );
};

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gap: backrs.spacing['spacing-3xl'],
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: `repeat(auto-fit, ${BASE_NUGGET_WIDTH})`,
  },
  alignItems: 'start',
  justifyContent: 'start',
  pb: backrs.spacing['spacing-xl'],
});

const loadingMoreSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: backrs.spacing['spacing-lg'],
  height: 64,
});

const loadingMoreAnimationSx: SxProps<Theme> = {
  width: 40,
  height: 40,
};
